// webapp/frontend/src/pages/Home.js
// Outcome-first landing page — no technical jargon, pure creator benefit

import { useState, useEffect } from "react";
import { Link } from "react-router-dom";

const TEAL = "#0D9488";
const TEAL_DIM = "rgba(13,148,136,0.12)";
const TEAL_BORDER = "rgba(13,148,136,0.25)";
const BG = "#06060a";
const TEXT = "#f0ede8";
const MUTED = "rgba(240,237,232,0.45)";
const SUBTLE = "rgba(240,237,232,0.08)";

const PLANS = [
  {
    name: "Starter",
    price: "$29",
    desc: "Everything you need to start protecting your income.",
    features: [
      "Automatic leak detection across 30+ piracy platforms",
      "DMCA takedowns filed for you",
      "Takedown tracker — see every removal",
      "Free scan included",
      "3-day free trial",
    ],
    cta: "Start free trial",
    href: "/signup?plan=starter",
    featured: false,
  },
  {
    name: "Pro",
    price: "$59",
    desc: "Maximum protection for creators serious about their income.",
    features: [
      "Everything in Starter",
      "15 protected master slots (vs 5)",
      "Priority enforcement: host escalations, manual site sweeps, follow-up notices",
      "X & social platform takedowns",
      "Bing deindexing (coming soon)",
      "Connect Dropbox / Google Drive: whole-catalog fingerprinting (watermarking when available) (coming soon)",
      "Buyer-level leak tracing for customs & direct sales (coming soon)",
      "Identity & dox threat monitoring (coming soon)",
      "Dark web surveillance (coming soon)",
      "Priority support",
      "3-day free trial",
    ],
    cta: "Start free trial",
    href: "/signup?plan=pro",
    featured: true,
  },
  {
    name: "Clean Slate",
    price: "$49",
    desc: "Already been leaked? We'll find every copy and remove it.",
    features: [
      "Upload content you want gone",
      "We hunt down every copy — including old ones",
      "Automatic removal requests filed",
      "Private dashboard tracking every takedown",
      "3-day free trial",
    ],
    cta: "Start clean",
    href: "/signup?plan=clean_slate",
    featured: false,
  },
];

const STATS = [
  { n: "3,184", label: "videos protected" },
  { n: "973+", label: "stolen copies removed" },
  { n: "30+", label: "piracy platforms monitored" },
  { n: "24hrs", label: "average removal time" },
];

const OUTCOMES = [
  {
    icon: "◎",
    title: "Your stolen content gets removed",
    body: "The moment your content appears somewhere it shouldn't, we act. Takedown notices are filed automatically — no forms, no lawyers, no chasing. You just see it disappear.",
  },
  {
    icon: "◈",
    title: "You find out who leaked it",
    body: "On Pro, we can tell you which subscriber your leak came from. Not just a guess — a ranked list of the most likely sources based on the evidence. Now you have receipts.",
  },
  {
    icon: "◉",
    title: "Your identity stays private (coming soon)",
    body: "When our identity-monitoring layer ships, we'll watch the places people try to expose creators — forums, dox sites, dark web boards. If your real name, address, or personal details surface anywhere, you'll know immediately.",
  },
  {
    icon: "◇",
    title: "You stop losing subscribers to free leaks",
    body: "When your content is free somewhere else, paying fans cancel. We close that gap — finding and removing stolen copies before they cost you income.",
  },
];

function useCountUp(target, duration = 1400) {
  const [val, setVal] = useState("0");
  useEffect(() => {
    const num = parseInt(target.replace(/\D/g, ""));
    if (!num) { setVal(target); return; }
    const suffix = target.replace(/[\d,]/g, "");
    let start = null;
    const step = (ts) => {
      if (!start) start = ts;
      const p = Math.min((ts - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.floor(eased * num).toLocaleString() + suffix);
      if (p < 1) requestAnimationFrame(step);
      else setVal(target);
    };
    requestAnimationFrame(step);
  }, [target, duration]);
  return val;
}

function StatCard({ n, label }) {
  const val = useCountUp(n);
  return (
    <div style={{ textAlign: "center" }}>
      <div style={{ fontFamily: "'DM Serif Display', serif", fontSize: "clamp(1.8rem, 4vw, 2.6rem)", color: TEAL, lineHeight: 1 }}>{val}</div>
      <div style={{ fontSize: 13, color: MUTED, marginTop: 8, lineHeight: 1.5 }}>{label}</div>
    </div>
  );
}

export default function Home() {
  return (
    <div style={{ background: BG, color: TEXT, fontFamily: "'DM Sans', sans-serif", minHeight: "100vh", overflowX: "hidden" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500&family=DM+Serif+Display:ital@0;1&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        a { text-decoration: none; color: inherit; }
        ::selection { background: rgba(13,148,136,0.3); }

        .nav-link { font-size: 14px; color: ${MUTED}; transition: color 0.2s; }
        .nav-link:hover { color: ${TEXT}; }

        .btn-primary {
          background: ${TEAL}; color: #fff; border: none; border-radius: 8px;
          padding: 13px 28px; font-size: 14px; font-weight: 500; cursor: pointer;
          font-family: inherit; transition: all 0.2s; display: inline-block; white-space: nowrap;
        }
        .btn-primary:hover { background: #0a7a70; transform: translateY(-1px); }

        .btn-ghost {
          background: transparent; color: ${MUTED}; border: 0.5px solid rgba(240,237,232,0.15);
          border-radius: 8px; padding: 13px 28px; font-size: 14px; cursor: pointer;
          font-family: inherit; transition: all 0.2s; display: inline-block; white-space: nowrap;
        }
        .btn-ghost:hover { color: ${TEXT}; border-color: rgba(240,237,232,0.35); }

        .outcome-card {
          background: rgba(255,255,255,0.02);
          border: 0.5px solid ${SUBTLE};
          border-radius: 16px; padding: 36px 32px;
          transition: border-color 0.3s, background 0.3s;
        }
        .outcome-card:hover { border-color: ${TEAL_BORDER}; background: rgba(13,148,136,0.03); }

        .plan-card {
          background: rgba(255,255,255,0.02); border: 0.5px solid ${SUBTLE};
          border-radius: 16px; padding: 32px; display: flex; flex-direction: column;
          transition: border-color 0.3s;
        }
        .plan-card:hover { border-color: ${TEAL_BORDER}; }
        .plan-card.featured { border-color: ${TEAL_BORDER}; background: rgba(13,148,136,0.04); }

        .check { color: ${TEAL}; margin-right: 10px; flex-shrink: 0; font-size: 13px; }

        .fade-in { animation: fadeUp 0.8s ease both; }
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        .pulse { animation: pulse 3s ease-in-out infinite; }
        @keyframes pulse {
          0%, 100% { opacity: 0.5; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.05); }
        }

        @media (max-width: 768px) {
          .hide-mobile { display: none !important; }
          .hero-btns { flex-direction: column !important; align-items: stretch !important; }
          .hero-btns a { text-align: center; }
          .grid-2 { grid-template-columns: 1fr !important; }
          .grid-3 { grid-template-columns: 1fr !important; }
          .grid-4 { grid-template-columns: repeat(2, 1fr) !important; }
        }
      `}</style>

      {/* ── Nav ── */}
      <nav style={{ position: "sticky", top: 0, zIndex: 100, borderBottom: `0.5px solid ${SUBTLE}`, backdropFilter: "blur(16px)", background: "rgba(6,6,10,0.9)" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", padding: "0 24px", height: 60, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Link to="/" style={{ fontFamily: "'DM Serif Display', serif", fontSize: 20, color: TEXT }}>
            Creator<span style={{ color: TEAL }}>Protect</span>
          </Link>
          <div style={{ display: "flex", alignItems: "center", gap: 28 }}>
            <Link to="/pricing" className="nav-link hide-mobile">Pricing</Link>
            <Link to="/cs-upload" className="nav-link hide-mobile">Clean Slate</Link>
            <Link to="/scan" className="nav-link">Free scan</Link>
            <Link to="/login" className="nav-link">Log in</Link>
            <Link to="/signup" className="btn-primary" style={{ padding: "8px 20px", fontSize: 13 }}>Start free trial</Link>
          </div>
        </div>
      </nav>

      {/* ── Hero ── */}
      <section style={{ maxWidth: 740, margin: "0 auto", padding: "110px 24px 90px", textAlign: "center" }}>

        <div className="fade-in" style={{ animationDelay: "0s", marginBottom: 32 }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: TEAL_DIM, border: `0.5px solid ${TEAL_BORDER}`, borderRadius: 20, padding: "6px 16px", fontSize: 12, color: TEAL, fontWeight: 500 }}>
            <span className="pulse" style={{ display: "inline-block", width: 6, height: 6, borderRadius: "50%", background: TEAL }} />
            973+ stolen copies removed this month
          </div>
        </div>

        <div className="fade-in" style={{ animationDelay: "0.1s" }}>
          <h1 style={{ fontFamily: "'DM Serif Display', serif", fontSize: "clamp(2.4rem, 6vw, 4rem)", fontWeight: 400, lineHeight: 1.15, marginBottom: 28, letterSpacing: "-0.02em" }}>
            Built by creators.<br />For creators.
          </h1>
        </div>

        <div className="fade-in" style={{ animationDelay: "0.2s" }}>
          <p style={{ fontSize: "clamp(16px, 2.5vw, 19px)", color: MUTED, lineHeight: 1.8, marginBottom: 20, maxWidth: 620, margin: "0 auto 20px" }}>
            Creator Protect is the all-in-one platform to protect your content,
            sell directly to your fans, and manage your videos — all in one place.
          </p>
          <p style={{ fontSize: "clamp(15px, 2.2vw, 17px)", color: TEXT, lineHeight: 1.7, marginBottom: 44, maxWidth: 620, margin: "0 auto 44px", fontWeight: 500 }}>
            Every video is piracy-proof with our industry-leading leak detection system.
            We go further than any platform dares to go.
          </p>
        </div>

        <div className="fade-in hero-btns" style={{ animationDelay: "0.3s", display: "flex", gap: 12, justifyContent: "center", marginBottom: 18 }}>
          <Link to="/signup" className="btn-primary" style={{ fontSize: 15, padding: "15px 34px" }}>Start your free trial</Link>
          <Link to="/scan" className="btn-ghost" style={{ fontSize: 15, padding: "15px 34px" }}>Free scan — no account needed</Link>
        </div>

        <div className="fade-in" style={{ animationDelay: "0.4s" }}>
          <p style={{ fontSize: 12, color: "rgba(240,237,232,0.22)" }}>3-day free trial · No credit card required · Cancel anytime</p>
        </div>
      </section>

      {/* ── Stats ── */}
      <section style={{ borderTop: `0.5px solid ${SUBTLE}`, borderBottom: `0.5px solid ${SUBTLE}`, padding: "56px 24px" }}>
        <div className="grid-4" style={{ maxWidth: 860, margin: "0 auto", display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 32 }}>
          {STATS.map((s, i) => <StatCard key={i} {...s} />)}
        </div>
      </section>

      {/* ── Problem ── */}
      <section style={{ maxWidth: 680, margin: "0 auto", padding: "100px 24px 80px", textAlign: "center" }}>
        <h2 style={{ fontFamily: "'DM Serif Display', serif", fontSize: "clamp(1.8rem, 4vw, 2.8rem)", fontWeight: 400, lineHeight: 1.3, marginBottom: 28 }}>
          Every day your content is out there for free, you're losing paid subscribers.
        </h2>
        <p style={{ fontSize: 17, color: MUTED, lineHeight: 1.85, marginBottom: 20 }}>
          Someone subscribes, downloads your content, and posts it somewhere free. Your other fans find it. They cancel. You never know why.
        </p>
        <p style={{ fontSize: 17, color: MUTED, lineHeight: 1.85 }}>
          Most creators have no idea how much this is costing them — because they have no way to see it happening. We built Creator Protect to change that.
        </p>
      </section>

      {/* ── Outcomes ── */}
      <section style={{ background: "rgba(255,255,255,0.01)", borderTop: `0.5px solid ${SUBTLE}`, borderBottom: `0.5px solid ${SUBTLE}`, padding: "100px 24px" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 64 }}>
            <div style={{ fontSize: 11, fontWeight: 500, color: TEAL, letterSpacing: "0.1em", marginBottom: 16 }}>WHAT YOU GET</div>
            <h2 style={{ fontFamily: "'DM Serif Display', serif", fontSize: "clamp(1.8rem, 4vw, 2.8rem)", fontWeight: 400 }}>
              Real results. Not promises.
            </h2>
          </div>
          <div className="grid-2" style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 20 }}>
            {OUTCOMES.map((o, i) => (
              <div key={i} className="outcome-card">
                <div style={{ fontSize: 22, color: TEAL, marginBottom: 18 }}>{o.icon}</div>
                <h3 style={{ fontSize: 19, fontWeight: 500, marginBottom: 12, lineHeight: 1.3 }}>{o.title}</h3>
                <p style={{ fontSize: 15, color: MUTED, lineHeight: 1.8 }}>{o.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Built for creators ── */}
      <section style={{ maxWidth: 680, margin: "0 auto", padding: "100px 24px", textAlign: "center" }}>
        <div style={{ fontSize: 11, fontWeight: 500, color: TEAL, letterSpacing: "0.1em", marginBottom: 20 }}>WHY WE BUILT THIS</div>
        <h2 style={{ fontFamily: "'DM Serif Display', serif", fontSize: "clamp(1.8rem, 4vw, 2.8rem)", fontWeight: 400, lineHeight: 1.3, marginBottom: 28 }}>
          We're creators too.<br />This happened to us.
        </h2>
        <p style={{ fontSize: 17, color: MUTED, lineHeight: 1.85, marginBottom: 20 }}>
          Stolen content, nowhere to turn, tools that didn't work or cost a fortune. We wanted something that actually solved the problem — not a half-measure that required hours of manual work every week.
        </p>
        <p style={{ fontSize: 17, color: MUTED, lineHeight: 1.85 }}>
          Creator Protect is what we wished existed. It runs in the background, protects your income, and only asks for your attention when something needs it.
        </p>
      </section>

      {/* ── Clean Slate ── */}
      <section style={{ background: "rgba(13,148,136,0.04)", borderTop: `0.5px solid ${TEAL_BORDER}`, borderBottom: `0.5px solid ${TEAL_BORDER}`, padding: "90px 24px" }}>
        <div style={{ maxWidth: 660, margin: "0 auto", textAlign: "center" }}>
          <div style={{ fontSize: 11, fontWeight: 500, color: TEAL, letterSpacing: "0.1em", marginBottom: 20 }}>CLEAN SLATE</div>
          <h2 style={{ fontFamily: "'DM Serif Display', serif", fontSize: "clamp(1.8rem, 4vw, 2.8rem)", fontWeight: 400, lineHeight: 1.3, marginBottom: 20 }}>
            The damage is done.<br />Let's undo it.
          </h2>
          <p style={{ fontSize: 17, color: MUTED, lineHeight: 1.85, marginBottom: 16 }}>
            If your content is already out there, Clean Slate is for you. Upload what you want gone. We'll track down every copy across the internet — old and new — and get them removed.
          </p>
          <p style={{ fontSize: 17, color: MUTED, lineHeight: 1.85, marginBottom: 44 }}>
            No judgment. Just results.
          </p>
          <Link to="/signup?plan=clean_slate" className="btn-primary" style={{ fontSize: 15, padding: "15px 34px" }}>
            Start Clean Slate — $49/mo
          </Link>
        </div>
      </section>

      {/* ── Pricing ── */}
      <section style={{ padding: "100px 24px" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 64 }}>
            <div style={{ fontSize: 11, fontWeight: 500, color: TEAL, letterSpacing: "0.1em", marginBottom: 16 }}>PRICING</div>
            <h2 style={{ fontFamily: "'DM Serif Display', serif", fontSize: "clamp(1.8rem, 4vw, 2.8rem)", fontWeight: 400, marginBottom: 12 }}>
              Simple. No surprises.
            </h2>
            <p style={{ fontSize: 16, color: MUTED }}>Try any plan free for 3 days. No credit card needed to start.</p>
          </div>
          <div className="grid-3" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 20 }}>
            {PLANS.map((p, i) => (
              <div key={i} className={`plan-card${p.featured ? " featured" : ""}`}>
                {p.featured && (
                  <div style={{ fontSize: 11, fontWeight: 500, color: TEAL, letterSpacing: "0.08em", marginBottom: 16 }}>MOST POPULAR</div>
                )}
                <div style={{ marginBottom: 28 }}>
                  <div style={{ fontSize: 15, fontWeight: 500, marginBottom: 10 }}>{p.name}</div>
                  <div style={{ fontFamily: "'DM Serif Display', serif", fontSize: 42, lineHeight: 1, marginBottom: 10 }}>
                    {p.price}<span style={{ fontSize: 15, color: MUTED, fontFamily: "'DM Sans', sans-serif", fontWeight: 400 }}>/mo</span>
                  </div>
                  <div style={{ fontSize: 14, color: MUTED, lineHeight: 1.6 }}>{p.desc}</div>
                </div>
                <div style={{ flex: 1, marginBottom: 32 }}>
                  {p.features.map((f, j) => (
                    <div key={j} style={{ display: "flex", alignItems: "flex-start", marginBottom: 14, fontSize: 14, color: "rgba(240,237,232,0.7)", lineHeight: 1.5 }}>
                      <span className="check">✓</span>{f}
                    </div>
                  ))}
                </div>
                <Link to={p.href} className={p.featured ? "btn-primary" : "btn-ghost"} style={{ textAlign: "center", display: "block", padding: "14px" }}>
                  {p.cta}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Final CTA ── */}
      <section style={{ borderTop: `0.5px solid ${SUBTLE}`, padding: "110px 24px", textAlign: "center" }}>
        <div style={{ maxWidth: 580, margin: "0 auto" }}>
          <h2 style={{ fontFamily: "'DM Serif Display', serif", fontSize: "clamp(2rem, 5vw, 3.4rem)", fontWeight: 400, lineHeight: 1.2, marginBottom: 24 }}>
            Your content is yours.<br /><em style={{ color: TEAL }}>Let's keep it that way.</em>
          </h2>
          <p style={{ fontSize: 17, color: MUTED, lineHeight: 1.85, marginBottom: 44 }}>
            Set up in 5 minutes. Protection runs every day after that — quietly, in the background, without you thinking about it.
          </p>
          <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap", marginBottom: 20 }}>
            <Link to="/signup" className="btn-primary" style={{ fontSize: 15, padding: "15px 34px" }}>Start your free trial</Link>
            <Link to="/scan" className="btn-ghost" style={{ fontSize: 15, padding: "15px 34px" }}>Free scan first</Link>
          </div>
          <p style={{ fontSize: 12, color: "rgba(240,237,232,0.2)" }}>3-day free trial · No credit card required · Cancel anytime</p>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer style={{ borderTop: `0.5px solid ${SUBTLE}`, padding: "40px 24px" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 16 }}>
          <div style={{ fontFamily: "'DM Serif Display', serif", fontSize: 18 }}>
            Creator<span style={{ color: TEAL }}>Protect</span>
          </div>
          <div style={{ display: "flex", gap: 24, flexWrap: "wrap", alignItems: "center" }}>
            {[["Privacy", "/privacy"], ["Terms", "/terms"], ["DMCA", "/dmca"], ["Contact", "/contact"]].map(([label, href]) => (
              <Link key={label} to={href} style={{ fontSize: 13, color: MUTED }}>{label}</Link>
            ))}
            <a
              href="https://x.com/CreatorProtekt"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Creator Protect on X"
              style={{ fontSize: 13, color: MUTED, display: "inline-flex", alignItems: "center", gap: 6 }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M18.244 2H21.5l-7.5 8.573L22.5 22h-6.86l-5.375-6.96L3.9 22H.64l8.02-9.166L.5 2h7.03l4.86 6.42L18.244 2zm-1.203 18h1.797L7.045 4H5.122L17.041 20z"/>
              </svg>
              @CreatorProtekt
            </a>
          </div>
          <div style={{ fontSize: 12, color: "rgba(240,237,232,0.2)" }}>© 2025 Creator Protect</div>
        </div>
      </footer>
    </div>
  );
}
